const isScientificNotation = (value: string | number) => {
  const scientificNotationRegex = /^-?\d+(\.\d+)?[eE][-+]?\d+$/;
  return scientificNotationRegex.test(value.toString());
};

export const formatLargeNumber = (num: number | string, decimalLength = 0) => {
  if (typeof num !== 'string' && typeof num !== 'number') return '0';
  if (typeof num === 'number' && isNaN(num)) return '0';
  if (isScientificNotation(num)) return (+num).toFixed(decimalLength);

  const [integerPart, decimalPart = ''] = num.toString().split('.');
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  if (decimalLength === 0) {
    return formattedInteger;
  }

  // Pad decimal part with zeros if needed
  const paddedDecimal = decimalPart.padEnd(decimalLength, '0').slice(0, decimalLength);
  return `${formattedInteger}.${paddedDecimal}`;
};

export const formatCompactNumber = (num: number | string): string => {
  // Convert to number if string
  const value = typeof num === 'string' ? parseFloat(num) : num;

  if (typeof value !== 'number' || isNaN(value)) return '0';
  if (!isFinite(value)) return '0'; // Handle Infinity

  // Handle negative numbers
  const isNegative = value < 0;
  const absValue = Math.abs(value);

  // First, round to 2 decimal places to see what we're actually displaying
  const displayValue = parseFloat(absValue.toFixed(2));

  // If the display value is less than 1000, show it without suffix
  if (displayValue < 1000) {
    // Remove trailing zeros by converting to string
    const formatted = displayValue.toString();
    return isNegative ? `-${formatted}` : formatted;
  }

  // Define the thresholds and suffixes
  const thresholds = [
    { value: 1e12, suffix: 't' }, // trillion
    { value: 1e9, suffix: 'b' }, // billion
    { value: 1e6, suffix: 'm' }, // million
    { value: 1e3, suffix: 'k' }, // thousand
  ];

  // Find the appropriate threshold
  for (const threshold of thresholds) {
    if (displayValue >= threshold.value) {
      const result = displayValue / threshold.value;

      // Format with up to 2 decimal places, removing trailing zeros
      // This handles: 3000 -> "3k", 3100 -> "3.1k", 3120 -> "3.12k"
      const formatted = parseFloat(result.toFixed(2)).toString() + threshold.suffix;
      return isNegative ? `-${formatted}` : formatted;
    }
  }

  // This should never be reached, but just in case
  return displayValue.toString();
};
